import React, { useCallback, useState } from 'react';

import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';

import { useDeleteBundle, useUpdateBundle } from '~/hooks/apis/accommodation/useBundles';
import useAppNavigation from '~/hooks/useAppNavigation';

import { Bundle, UpdateBundlePayload } from '~/services/AccommodationService';

import { ALL_BUNDLE_STATUSES } from '../constants';

interface Props {
  bundle: Bundle;
}

function AccommodationBundleContentTab({ bundle }: Props) {
  const { push } = useAppNavigation();
  const [name, setName] = useState(bundle.name);
  const [status, setStatus] = useState<NonNullable<UpdateBundlePayload['status']>>(
    bundle.status as NonNullable<UpdateBundlePayload['status']>,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const updateBundleMutation = useUpdateBundle();
  const deleteBundleMutation = useDeleteBundle();

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);
      try {
        await updateBundleMutation.mutateAsync({
          bundleId: bundle.id,
          payload: { name, status },
        });
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save bundle');
      }
    },
    [name, status, updateBundleMutation, bundle.id],
  );

  const handleDelete = useCallback(async () => {
    if (!window.confirm(`Soft-delete bundle "${bundle.name}"?`)) return;
    try {
      await deleteBundleMutation.mutateAsync({ bundleId: bundle.id });
      push('/accommodation/properties#bundles');
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to delete bundle');
    }
  }, [deleteBundleMutation, bundle.id, bundle.name, push]);

  const isSubmitting = updateBundleMutation.isPending;
  const isDeleting = deleteBundleMutation.isPending;

  return (
    <Box>
      <Box component="form" onSubmit={handleSave}>
        <Stack spacing={2}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <TextField label="Bundle Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as NonNullable<UpdateBundlePayload['status']>)}
            >
              {ALL_BUNDLE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save Content'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Box mt={6} pt={3} borderTop={1} borderColor="divider">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Box fontWeight={500}>Danger zone</Box>
            <Box color="text.secondary" fontSize={14}>
              Soft-delete this bundle. Recovery is possible by Engineering only.
            </Box>
          </Box>
          <Button color="error" variant="outlined" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete Bundle'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default AccommodationBundleContentTab;
